import { useTranslation } from 'next-i18next';
import { type AlertDialogProps, Button, type ButtonProps, DefaultDialog, type DefaultDialogProps } from '@/ui_v3';
import { createDialogManager } from '@/ui_v3/components/dialog/utils';
import { cn } from '@/utils/tailwind.util';

/**
 * 다이얼로그를 간편하게 열 수 있는 함수
 *
 * @example
 * // 기본 확인 버튼만 (클릭 시 다이얼로그 닫힘)
 * openFlexibleDialog({
 *   body: "삭제하시겠습니까?",
 *   footerButtons: [{ kind: 'confirm' }]
 * });
 *
 * @example
 * // 취소 + 확인 조합
 * openFlexibleDialog({
 *   body: "저장하시겠습니까?",
 *   footerButtons: [
 *     { children: '취소', useClose: true },
 *     { kind: 'confirm', children: '저장', intent: 'primary' }
 *   ]
 * });
 *
 * @example
 * // 완전 커스텀 버튼들 (조건부 닫기 포함)
 * openFlexibleDialog({
 *   body: "작업을 선택하세요",
 *   footerButtons: [
 *     { children: '삭제', intent: 'danger', onClick: handleDelete, useClose: true },
 *     { children: '수정', intent: 'primary', onClick: handleEdit, useClose: false },
 *     {
 *       children: '저장',
 *       onClick: handleSave,
 *       useClose: () => validateForm() // 폼이 유효할 때만 닫기
 *     }
 *   ]
 * });
 *
 * @example
 * // 빈 경우 자동으로 기본 확인 버튼 표시
 * openFlexibleDialog({ body: "알림입니다" });
 */
export const openFlexibleDialog = (payloads: FlexibleDialogProps) => {
  return flexDialog.openAsync(payloads);
};
export const flexDialog = createDialogManager<AlertDialogProps>(options => <FlexibleDialog {...options} />);

/**
 * 커스텀 버튼 타입 - 사용자 정의 동작 가능
 */
type CustomFooterButton = ButtonProps & {
  useClose: boolean | (() => boolean); // 클릭 시 다이얼로그를 닫을지 여부 (함수로 조건부 가능)
};
type DefaultFooterButton = Partial<ButtonProps> & {
  /**
   * 기본 확인 버튼 타입 - 클릭 시 자동으로 다이얼로그를 닫음
   */
  kind: 'confirm';
};

/**
 * 다이얼로그 푸터 버튼 타입
 */
type FooterButton = CustomFooterButton | DefaultFooterButton;

/**
 * 유연한 다이얼로그 Props
 */
export interface FlexibleDialogProps extends Omit<DefaultDialogProps, 'footer'> {
  /** 푸터 버튼 배열. 빈 경우 기본 확인 버튼 자동 생성 */
  footerButtons?: FooterButton[];
  /** 푸터 컨테이너 CSS 클래스(패딩은 기본 적용되어있어서 줄 필요없습니다) */
  footerClassName?: string;
}
export const FlexibleDialog = ({
  footerClassName,
  footerButtons,
  body,
  onClose,
  ...props
}: FlexibleDialogProps) => {
  const {
    t
  } = useTranslation(['common']);
  return <DefaultDialog body={typeof body === 'string' ? <div className="px-lg max-mobile-lg:px-md">{body}</div> : body} footer={<div className={cn('flex flex-row gap-sm', footerClassName)}>
          {footerButtons != null && footerButtons.length > 0 ? footerButtons.map((button, index) => {
      if ('kind' in button && button.kind === 'confirm') {
        return <Button key={index} className={cn('w-full', button.className)} size={button.size ?? 'md'} intent={button.intent ?? 'strong'} onClick={() => onClose?.(true)}>
                    {button.children ?? t('common.alertDialog.confirmText')}
                  </Button>;
      }
      return <Button key={index} className={button.className} size={button.size} intent={button.intent} onClick={e => {
        button.onClick?.(e);
        if ('useClose' in button) {
          const shouldClose = typeof button.useClose === 'function' ? button.useClose() : button.useClose;
          if (shouldClose) {
            onClose?.(true);
          }
        }
      }}>
                  {button.children}
                </Button>;
    }) : <Button className="w-full" size="md" intent="strong" onClick={() => onClose?.(true)}>
              {t('common.alertDialog.confirmText')}
            </Button>}
        </div>} {...props} data-sentry-element="DefaultDialog" data-sentry-component="FlexibleDialog" data-sentry-source-file="FlexibleDialog.tsx" />;
};